import { DateTime } from "luxon";

import { FIELD_NAME_DATE, FIELD_NAME_TIME_RANGE } from "../../atoms/Form";
import {
  type DayOfWeek,
  type FormValues,
  type MeetingTimeData,
  type MeetingTimeSlot,
} from "./AddEventTimesModal.types";

// Centralized strings for internationalization
export const STRINGS = {
  tAddAnotherTime: "Add Another Time",
  tAddEventTimes: "Create",
  tCancel: "Cancel",
  tCapacity: "Capacity",
  tCreate: "Create Event Times",
  tDate: "Date",
  tMeetingTime: "Event Time",
  tRemoveTime: "Remove Time",
  tReset: "Reset",
  tSeries: "Series",
  tTimeRange: "Time Range",
} as const;

export const DAYS_OF_WEEK: readonly DayOfWeek[] = [
  { fullName: "Sunday", key: 0, label: "S" },
  { fullName: "Monday", key: 1, label: "M" },
  { fullName: "Tuesday", key: 2, label: "T" },
  { fullName: "Wednesday", key: 3, label: "W" },
  { fullName: "Thursday", key: 4, label: "T" },
  { fullName: "Friday", key: 5, label: "F" },
  { fullName: "Saturday", key: 6, label: "S" },
] as const;

export const generateFieldName = (id: string, field: string): string => `meetingTime_${id}_${field}`;

export const getUserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getNextSelectedDay = (currentDate: DateTime, selectedDays: Set<number>): DateTime => {
  if (selectedDays.size === 0) {
    // If no days selected, just add 1 day
    return currentDate.plus({ days: 1 });
  }

  const selectedDaysArray = Array.from(selectedDays).sort();
  // Convert Luxon weekday (1=Monday, 7=Sunday) to dayjs format (0=Sunday, 6=Saturday)
  const currentDayOfWeek = currentDate.weekday === 7 ? 0 : currentDate.weekday;

  // Find the next selected day in the same week
  const nextDayInWeek = selectedDaysArray.find((day) => day > currentDayOfWeek);

  if (nextDayInWeek !== undefined) {
    // Found a day later this week
    const daysToAdd = nextDayInWeek - currentDayOfWeek;
    return currentDate.plus({ days: daysToAdd });
  } else {
    // No more days this week, wrap to next week's first selected day
    const firstSelectedDay = selectedDaysArray[0];
    const daysToAdd = 7 - currentDayOfWeek + firstSelectedDay;
    return currentDate.plus({ days: daysToAdd });
  }
};

export const isMeetingTimeComplete = (slotId: string, formValues: FormValues): boolean => {
  const date = formValues[generateFieldName(slotId, FIELD_NAME_DATE)] as DateTime | null;
  const timeRange = formValues[generateFieldName(slotId, FIELD_NAME_TIME_RANGE)] as [DateTime | null, DateTime | null];
  const capacity = formValues[generateFieldName(slotId, "capacity")];

  // First check if all fields are filled
  if (!date || !timeRange || !timeRange[0] || !timeRange[1] || capacity === undefined) {
    return false;
  }

  const now = DateTime.now();
  const userTimezone = getUserTimezone();
  const localDate = date.setZone(userTimezone);
  const localStartTime = timeRange[0].setZone(userTimezone);
  const localEndTime = timeRange[1].setZone(userTimezone);

  // Combine date with times for proper comparison
  const startDateTime = localDate.set({
    hour: localStartTime.hour,
    millisecond: 0,
    minute: localStartTime.minute,
    second: 0,
  });

  const endDateTime = localDate.set({
    hour: localEndTime.hour,
    millisecond: 0,
    minute: localEndTime.minute,
    second: 0,
  });

  // Check if date is in the past
  if (localDate < now.startOf("day")) {
    return false;
  }

  // If it's today, check if times are in the past
  if (localDate.hasSame(now, "day")) {
    if (startDateTime < now || endDateTime < now) {
      return false;
    }
  }

  return true;
};

export const generateRequiredFields = (meetingTimeSlots: MeetingTimeSlot[]): string[] => {
  const fields: string[] = [];
  for (const slot of meetingTimeSlots) {
    fields.push(
      generateFieldName(slot.id, FIELD_NAME_DATE),
      generateFieldName(slot.id, FIELD_NAME_TIME_RANGE),
      generateFieldName(slot.id, "capacity"),
    );
  }
  return fields;
};

export const extractMeetingTimesFromForm = (
  formValues: FormValues,
  meetingTimeSlots: MeetingTimeSlot[],
): MeetingTimeData[] => {
  const meetingTimes: MeetingTimeData[] = [];
  const userTimezone = getUserTimezone();

  for (const slot of meetingTimeSlots) {
    const dateFieldName = generateFieldName(slot.id, FIELD_NAME_DATE);
    const timeRangeFieldName = generateFieldName(slot.id, FIELD_NAME_TIME_RANGE);
    const capacityFieldName = generateFieldName(slot.id, "capacity");
    const seriesIdFieldName = generateFieldName(slot.id, "seriesId");

    const date = formValues[dateFieldName] as DateTime | null;
    const timeRange = formValues[timeRangeFieldName] as [DateTime | null, DateTime | null];
    const capacity = formValues[capacityFieldName];
    const seriesId = formValues[seriesIdFieldName] as string | null;

    if (date && timeRange && timeRange[0] && timeRange[1] && capacity !== undefined && capacity !== null) {
      // Use the user's local timezone for proper DST handling
      const localDate = date.setZone(userTimezone);
      const localStartTime = timeRange[0].setZone(userTimezone);
      const localEndTime = timeRange[1].setZone(userTimezone);

      // Combine date with start/end times, preserving local time regardless of DST
      const startTime = localStartTime.set({
        day: localDate.day,
        month: localDate.month,
        year: localDate.year,
      });

      const endTime = localEndTime.set({
        day: localDate.day,
        month: localDate.month,
        year: localDate.year,
      });

      meetingTimes.push({
        capacity: Number(capacity),
        date: localDate,
        endTime,
        seriesId: seriesId || undefined,
        startTime,
      });
    }
  }

  return meetingTimes;
};

export const formatDateTimeForSummary = (date: DateTime, timeRange: [DateTime | null, DateTime | null]): string => {
  if (!date?.toFormat || !timeRange[0]?.toFormat || !timeRange[1]?.toFormat) {
    return "";
  }

  // Format the date as "Wednesday, July 16th 2025"
  const formattedDate = date.toFormat("cccc, MMMM d, yyyy");

  // Use the user's local timezone instead of hardcoding Eastern
  const userTimezone = getUserTimezone();

  const localStartTime = timeRange[0] ? timeRange[0].setZone(userTimezone) : null;
  const localEndTime = timeRange[1] ? timeRange[1].setZone(userTimezone) : null;

  // Format the times with abbreviated timezone (EST/EDT, PST/PDT, etc.)
  const startTime = localStartTime ? localStartTime.toFormat("h:mma") : "";
  const endTime = localEndTime ? localEndTime.toFormat("h:mma") : "";

  return `${formattedDate} at ${startTime} - ${endTime}`;
};

export const formatCapacityText = (capacity: unknown): string => {
  return Number(capacity) === 0 ? "Unlimited" : capacity?.toString() || "0";
};
